import { useState, useEffect, useRef, useCallback } from 'react';
import { APP_VERSION } from '@/lib/version';
import { hardReload } from '@/lib/hard-reload';
import { useVersionStore } from '@/stores/version-store';

const CHECK_INTERVAL = 60_000; // 1분마다 체크

/** 다음 로컬 자정(00:00:00, 브라우저 로컬 타임존 기준)까지 남은 ms */
function msUntilNextMidnight(): number {
  const now = new Date();
  const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return nextMidnight.getTime() - now.getTime();
}

/** 남은 ms를 "HH:MM:SS 후 자동 새로고침" 형태로 짧게 포맷 */
function formatCountdown(remainingMs: number): string {
  const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)} 후 자동 새로고침`;
}

/**
 * 배포된 version.json과 현재 앱 버전을 주기적으로 비교.
 * 새 버전이 감지되면 즉시 새로고침하지 않고 배너로 알려 사용자가 시점을 선택하게 한다.
 * 미조치 시 다음 로컬 자정에 자동으로 하드리로드한다.
 * 감지 상태는 version-store에 둔다 — NavigationReloadGate가 다음 화면 이동을 전체 로드로 바꾸는 근거.
 */
export function useVersionCheck() {
  const detected = useRef(false);
  const hasNewVersion = useVersionStore((s) => s.hasNewVersion);
  const markNewVersion = useVersionStore((s) => s.markNewVersion);
  const [countdownLabel, setCountdownLabel] = useState<string | null>(null);

  const check = useCallback(async () => {
    if (detected.current || useVersionStore.getState().hasNewVersion) return;

    try {
      // 브라우저 캐시를 우회하여 항상 최신 version.json을 가져옴
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;

      const { version } = await res.json();
      if (!version || version === APP_VERSION) return;

      console.log(`[VersionCheck] 새 버전 감지: ${APP_VERSION} → ${version}`);
      detected.current = true;
      markNewVersion(version);
    } catch {
      // 네트워크 실패 등 — 다음 주기에 재시도
    }
  }, [markNewVersion]);

  useEffect(() => {
    // 앱 시작 5초 후 첫 체크 (초기 로딩 방해 방지)
    const initialTimeout = setTimeout(check, 5_000);
    const intervalId = setInterval(check, CHECK_INTERVAL);

    // 탭 복귀 시에도 체크
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [check]);

  // 새 버전 감지 후: 다음 로컬 자정까지 1초 간격 카운트다운, 도달 시 자동 하드리로드.
  // 로그인은 access=메모리/refresh=httpOnly 쿠키 구조라 리로드 후에도 자동 재인증된다.
  useEffect(() => {
    if (!hasNewVersion) {
      setCountdownLabel(null);
      return;
    }

    // intervalId는 tick() 최초 호출(동기)에서도 참조되므로 let으로 미리 선언해
    // TDZ(자정 정각에 진입해 remaining<=0으로 즉시 분기되는 극단 케이스)를 피한다.
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const tick = () => {
      const remaining = msUntilNextMidnight();
      if (remaining <= 0) {
        setCountdownLabel(null);
        if (intervalId) clearInterval(intervalId);
        void hardReload();
        return;
      }
      setCountdownLabel(formatCountdown(remaining));
    };

    tick();
    intervalId = setInterval(tick, 1_000);

    // 백그라운드 탭에서는 setInterval이 스로틀되어 표시가 부정확할 수 있으므로
    // 탭 복귀 시 즉시 재계산 — 자정이 이미 지났다면 그 즉시 리로드된다.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasNewVersion]);

  const reload = useCallback(async () => {
    await hardReload();
  }, []);

  return { hasNewVersion, reload, countdownLabel };
}
